/**
 * Optional comment (+ file tags) when changing request stage.
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import { stageLabel } from '../../../utils/ideaStatusTransitions';
import RequestAttachmentPicker from '../components/RequestAttachmentPicker';
import '../AIGarage.css';

const StageTransitionModal = ({
  isOpen,
  ideaId,
  fromStatus,
  toStatus,
  title,
  confirmLabel = 'Confirm',
  variant = 'default',
  userEmail,
  userName,
  onClose,
  onConfirm,
}) => {
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setComment('');
    setFiles([]);
    setError(null);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || submitting) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      let uploaded = [];
      if (files.length > 0 && ideaId) {
        uploaded = (await garageAPI.uploadIdeaAttachments(ideaId, files)) || [];
      }
      const text = comment.trim();
      let fullComment = text;
      if (uploaded.length) {
        const tags = uploaded
          .map((a) => `📎 ${a.filename || a.name || 'file'}`)
          .join('\n');
        fullComment = fullComment ? `${fullComment}\n\n${tags}` : tags;
      }
      if (fromStatus || toStatus) {
        const fromL = fromStatus ? stageLabel(fromStatus) : '';
        const toL = toStatus ? stageLabel(toStatus) : '';
        const header =
          fromL && toL
            ? `**Stage change:** ${fromL} → ${toL}`
            : toL
              ? `**Stage:** ${toL}`
              : '';
        if (header) {
          fullComment = fullComment ? `${header}\n\n${fullComment}` : header;
        }
      }
      if ((text || uploaded.length > 0) && ideaId && userEmail) {
        try {
          await garageAPI.createComment({
            ideaId,
            text: fullComment,
            authorEmail: userEmail,
            authorName: userName,
            commentType: 'stage_transition',
          });
        } catch (commentErr) {
          console.warn('Stage transition comment failed:', commentErr);
        }
      }
      await onConfirm?.({
        comment: fullComment,
        commentText: text,
        attachments: uploaded,
        toStatus,
      });
      onClose?.();
    } catch (err) {
      console.error('Stage transition failed:', err);
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'string' && detail) ||
          err?.message ||
          'Could not complete this stage change.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="garage-modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="garage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>{title || 'Update stage'}</h2>
          <button
            type="button"
            className="garage-modal-close"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={20} />
          </button>
        </div>
        <div className="garage-modal-body">
          {(fromStatus || toStatus) && (
            <p className="garage-request-info-context">
              {fromStatus ? stageLabel(fromStatus) : '—'}
              {' → '}
              <strong>{toStatus ? stageLabel(toStatus) : '—'}</strong>
            </p>
          )}
          <form onSubmit={handleSubmit}>
            <label className="garage-form-label" htmlFor="stage-transition-comment">
              Comment (optional)
            </label>
            <textarea
              id="stage-transition-comment"
              className="garage-textarea"
              rows={4}
              placeholder="Add a note for the next stage. You can also attach files."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
            />
            <div style={{ marginTop: '0.75rem' }}>
              <RequestAttachmentPicker files={files} onChange={setFiles} disabled={submitting} />
            </div>
            {error ? <p className="garage-form-error">{error}</p> : null}
            <div className="garage-modal-actions" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`garage-btn ${
                  variant === 'danger' ? 'garage-btn-danger' : 'garage-btn-primary'
                }`}
                disabled={submitting}
              >
                {submitting ? 'Working…' : confirmLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default StageTransitionModal;
